import classnames from 'classnames';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

/**
 * Element groups that affect page layout or navigation, so each call site opts
 * into them explicitly rather than inheriting them by default.
 */
export type MarkdownFeature = 'headings' | 'links' | 'quotes' | 'tables';

/**
 * Elements rendered everywhere. Mirrors `App\Markdown\RestrictedMarkdownExtension`,
 * the canonical restricted set for the admin-authored copy that reaches this client,
 * plus the line breaks and strikethrough produced by the remark plugins below.
 */
const baseElements = ['p', 'br', 'em', 'strong', 'del', 'ul', 'ol', 'li', 'code'];

const optionalElements: Record<MarkdownFeature, string[]> = {
  headings: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  links: ['a'],
  quotes: ['blockquote', 'pre'],
  tables: ['table', 'thead', 'tbody', 'tr', 'th', 'td'],
};

const externalLinks: Components = {
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
};

interface MarkdownProps {
  /** Element groups to allow on top of the always-on base set. */
  allow?: MarkdownFeature[];
  /** Markdown source. Renders nothing when empty. */
  children?: string | null;
  className?: string;
  /** Overrides for how individual allowed elements render. */
  components?: Components;
}

/**
 * Renders markdown with a deny-by-default element allowlist.
 *
 * Raw HTML is never rendered: react-markdown drops it unless `rehype-raw` is added,
 * and its `defaultUrlTransform` strips unsafe protocols such as `javascript:` from
 * hrefs. Elements outside the allowlist are unwrapped rather than discarded, so
 * unsupported syntax degrades to its own text instead of disappearing.
 */
const Markdown = ({ allow = [], children = null, className, components }: MarkdownProps) => {
  if (!children) {
    return null;
  }

  return (
    <div className={classnames('markdown', className)}>
      <ReactMarkdown
        allowedElements={[...baseElements, ...allow.flatMap((feature) => optionalElements[feature])]}
        components={{ ...(allow.includes('links') ? externalLinks : {}), ...components }}
        remarkPlugins={[remarkGfm, remarkBreaks]}
        unwrapDisallowed
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default Markdown;
